import { Component } from '@mycolorway/tao';
import { MDCMenuSurface, Corner } from '@material/menu-surface';
import moment from 'moment';
import flatpickr from 'next/javascripts/modules/flatpickr';
import { closest } from 'next/javascripts/modules/dom';
import eventListenerMixin from 'next/javascripts/mixins/event-listener-mixin';

export default Component('tr-date-picker-field-list', {
  mixins: [eventListenerMixin],

  properties: {
    modeSwitchable: {
      type: Boolean,
      default: false,
      observer: 'syncAttrsToMenu',
    },

    shortcutEnabled: {
      type: Boolean,
      default: false,
      observer: 'syncAttrsToMenu',
    },

    showClearButton: {
      type: Boolean,
      default: false,
      observer: 'syncAttrsToMenu',
    },

    mode: {
      type: String,
      default: 'single',
      observer: 'modeChanged',
    },

    valueFormat: {
      type: String,
      default: 'Y-m-d',
    },

    appendMenuToBody: {
      type: Boolean,
      default: false,
    },

    animating: {
      type: Boolean,
      default: true,
    },

    selectedValue: {
      get() {
        return this.selectedDates
          .map(date => flatpickr.formatDate(date, this.valueFormat)).join(',');
      },
    },
  },

  ready() {
    this.selectedDates = [];

    this.menuSurfaceRoot = this.querySelector('.mdc-menu-surface');
    this.syncAttrsToMenu();

    this.modeSwitchLinks = this.menuSurfaceRoot.querySelector('.mode-switch-links');
    this.shortcuts = this.menuSurfaceRoot.querySelector('.shortcuts');
    this.linkClear = this.menuSurfaceRoot.querySelector('.link-clear');

    this.mdcMenuSurface = new MDCMenuSurface(this.menuSurfaceRoot);
    this.mdcMenuSurface.setAnchorCorner(Corner.BOTTOM_LEFT);
    this.mdcMenuSurface.quickOpen = !this.animating;

    this.initFlatpickr();
    this.addEventListeners();
  },

  disconnected() {
    this.stopListening();
    this.destroyFlatpickr();
    this.restoreMenuSurface();

    if (this.mdcMenuSurface) {
      this.mdcMenuSurface.quickOpen = true;
      this.mdcMenuSurface.destroy();
    }
  },

  beforeCache() {
    this.destroyFlatpickr();
    this.restoreMenuSurface();
  },

  restoreMenuSurface() {
    if (this.appendMenuToBody && this.menuSurfaceRoot && !this.contains(this.menuSurfaceRoot)) {
      this.menuSurfaceRoot.classList.remove(
        'mdc-menu-surface--open',
        'mdc-menu-surface--animating-closed',
      );
      this.appendChild(this.menuSurfaceRoot);
    }
  },

  initFlatpickr() {
    const config = {
      animate: false,
      mode: this.mode,
      static: true,
      inline: true,
      dateFormat: this.valueFormat,
      closeOnSelect: false,
      onChange: this.handleFlatpickrChange.bind(this),
    };

    this.flatpickrInput = this.menuSurfaceRoot.querySelector('.flatpickr-input-target');
    this.flatpickr = flatpickr(this.flatpickrInput, config);
  },

  destroyFlatpickr() {
    if (this.flatpickr) this.flatpickr.destroy();
    this.flatpickr = null;
  },

  setSelectedValue(value) {
    if (!value) return;

    let selectedDates = value.split(/,\s?/).filter(s => !!s);
    if (this.mode === 'range' && selectedDates.length !== 2) selectedDates = [];
    if (selectedDates.length === 2 && this.mode !== 'range') this.mode = 'range';

    this.flatpickr.setDate(selectedDates);
    this.selectedDates = [...this.flatpickr.selectedDates];
  },

  clearSelected() {
    this.selectedDates = [];
    this.flatpickr.clear();
  },

  elementInContainer(el) {
    return this.menuSurfaceRoot.contains(el);
  },

  syncAttrsToMenu() {
    this.menuSurfaceRoot.dataset.mode = this.mode;

    if (this.modeSwitchable) {
      this.menuSurfaceRoot.dataset.modeSwitchable = '';
    } else {
      delete this.menuSurfaceRoot.dataset.modeSwitchable;
    }

    if (this.shortcutEnabled) {
      this.menuSurfaceRoot.dataset.shortcutEnabled = '';
    } else {
      delete this.menuSurfaceRoot.dataset.shortcutEnabled;
    }

    if (this.showClearButton) {
      this.menuSurfaceRoot.dataset.showClearButton = '';
    } else {
      delete this.menuSurfaceRoot.dataset.showClearButton;
    }
  },

  modeChanged() {
    this.flatpickr.set('mode', this.mode);
    this.menuSurfaceRoot.dataset.mode = this.mode;

    const current = this.selectedDates[0] || new Date();
    if (this.mode === 'range') {
      this.flatpickr.setDate([current, moment(current).endOf('week').toDate()]);
    } else {
      this.flatpickr.setDate(current);
    }
  },

  isOpen() {
    return this.mdcMenuSurface.open;
  },

  open() {
    if (this.isOpen()) return;

    if (this.appendMenuToBody) this.mdcMenuSurface.hoistMenuToBody();

    this.namespacedTrigger('beforeOpen');
    if (this.mdcMenuSurface.open) this.mdcMenuSurface.quickOpen = true;
    this.mdcMenuSurface.open = true;
  },

  close() {
    if (this.isOpen()) this.mdcMenuSurface.open = false;
  },

  addEventListeners() {
    this.listenTo(this.mdcMenuSurface, {
      'MDCMenuSurface:opened': this.handleMenuSurfaceOpened.bind(this),
      'MDCMenuSurface:closed': this.handleMenuSurfaceClosed.bind(this),
    });

    this.listenTo(this.flatpickr.daysContainer, 'mousedown', () => {
      this.namespacedTrigger('mousedown');
    });

    this.listenTo(this.modeSwitchLinks, 'click', (event) => {
      const link = closest(event.target, '[data-mode]');
      if (link) this.mode = link.dataset.mode;
    });

    this.listenTo(this.shortcuts, 'click', this.handleShortcutClick.bind(this));

    this.listenTo(this.linkClear, 'click', () => this.namespacedTrigger('clear'));
  },

  setAnchor(anchor) {
    this.mdcMenuSurface.setMenuSurfaceAnchorElement(anchor);
  },

  setAnchorMargin(distance) {
    this.mdcMenuSurface.setAnchorMargin(distance);
  },

  handleMenuSurfaceOpened() {
    if (this.animating) this.mdcMenuSurface.quickOpen = false;
    this.namespacedTrigger('opened', {
      bubbles: true,
    });
  },

  handleMenuSurfaceClosed() {
    this.restoreMenuSurface();

    this.mode = this.selectedDates.length === 2 ? 'range' : 'single';
    this.flatpickr.setDate(this.selectedDates);
    this.namespacedTrigger('closed', {
      bubbles: true,
    });
  },

  handleFlatpickrChange(selectedDates) {
    if (selectedDates.length === 0) return;
    if (this.mode === 'range' && selectedDates.length === 1) return;

    this.selectedDates = [...selectedDates];
    this.namespacedTrigger('select', { detail: selectedDates });
  },

  handleShortcutClick(event) {
    const link = closest(event.target, '[data-shortcut]');
    const { shortcut } = link ? link.dataset : {};
    if (!shortcut) return;

    switch (shortcut) {
      case 'current':
        this.flatpickr.setDate(
          this.mode === 'range' ? [
            moment().startOf('week').toDate(),
            moment().endOf('week').toDate(),
          ] : moment().toDate(),
          true,
        );
        break;
      case 'next':
        this.flatpickr.setDate(
          this.mode === 'range' ? [
            moment().add(1, 'week').startOf('week').toDate(),
            moment().add(1, 'week').endOf('week').toDate(),
          ] : moment().add(1, 'day').toDate(),
          true,
        );
        break;
      case 'friday':
        this.flatpickr.setDate(moment().startOf('week').add('days', 4).toDate(), true);
        break;
      default:
        break;
    }
  },
});
